import { useLocation, useNavigate } from 'react-router-dom';
import type { CSSProperties } from 'react';
import { Icon, IconBtn, type IconName } from '@/components/primitives';
import type { AppRole } from '@/lib/scope';

interface NavItem {
  id: string;
  route: string;
  label: string;
  icon: IconName;
  indent?: number;
  disabled?: boolean;
  hint?: string;
}

interface NavSection {
  group: string;
  roles: AppRole[];
  items: NavItem[];
}

const NAV_SECTIONS: NavSection[] = [
  {
    group: 'Lập kế hoạch',
    roles: ['PLANNER', 'OPS', 'ADMIN', 'AUDITOR'],
    items: [
      { id: 'home', route: '/', label: 'Trang chủ', icon: 'home' },
      { id: 'fc-overview', route: '/planner/forecast', label: 'Dự báo', icon: 'forecast' },
      {
        id: 'fc-review',
        route: '/planner/forecast/review',
        label: 'Phê duyệt',
        icon: 'check',
        indent: 1,
      },
      {
        id: 'fc-customer',
        route: '/planner/forecast/customer',
        label: 'Khách hàng',
        icon: 'users',
        indent: 1,
      },
      {
        id: 'fc-warehouse',
        route: '/planner/forecast/warehouse',
        label: 'Kho',
        icon: 'warehouse',
        indent: 1,
      },
      { id: 'plan', route: '/planner/shift-plan', label: 'Kế hoạch ca', icon: 'plan' },
      { id: 'hiring', route: '/planner/hiring', label: 'Tuyển dụng', icon: 'hiring' },
      {
        id: 'override',
        route: '/planner/override-import',
        label: 'Gửi điều chỉnh',
        icon: 'upload',
      },
      {
        id: 'history-pl',
        route: '/admin/history-import',
        label: 'Dữ liệu lịch sử',
        icon: 'history',
      },
    ],
  },
  {
    group: 'Cấu hình',
    roles: ['PLANNER', 'OPS', 'ADMIN'],
    items: [
      { id: 'cfg-root', route: '/admin/config', label: 'Tham số vận hành', icon: 'sliders' },
      {
        id: 'cfg-dinhmuc',
        route: '/admin/config/dinhmuc',
        label: 'Định mức',
        icon: 'layers',
        indent: 1,
      },
      {
        id: 'cfg-thamso',
        route: '/admin/config/thamso',
        label: 'Tham số',
        icon: 'calendar',
        indent: 1,
      },
    ],
  },
  {
    group: 'Quản trị',
    roles: ['OPS', 'ADMIN'],
    items: [
      { id: 'warehouses', route: '/admin/warehouses', label: 'Kho', icon: 'warehouse' },
      { id: 'staff', route: '/admin/staff', label: 'Nhân sự', icon: 'users' },
      {
        id: 'history',
        route: '/admin/history-import',
        label: 'Dữ liệu lịch sử',
        icon: 'history',
      },
    ],
  },
  {
    group: 'Kiểm toán',
    roles: ['AUDITOR', 'ADMIN', 'OPS'],
    items: [
      {
        id: 'activity',
        route: '/audit/activity',
        label: 'Nhật ký hoạt động',
        icon: 'audit',
        disabled: true,
        hint: 'Sắp có',
      },
      {
        id: 'plans',
        route: '/audit/plans',
        label: 'Lưu trữ kế hoạch',
        icon: 'history',
        disabled: true,
        hint: 'Sắp có',
      },
    ],
  },
];

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
  role: AppRole;
  drawerOpen?: boolean;
  onCloseDrawer?: () => void;
  isMobile?: boolean;
}

export function Sidebar({
  collapsed,
  onToggle,
  role,
  drawerOpen,
  onCloseDrawer,
  isMobile,
}: SidebarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const route = location.pathname;
  const W = collapsed && !isMobile ? 64 : 224;

  function go(r: string) {
    navigate(r);
    if (isMobile && onCloseDrawer) onCloseDrawer();
  }

  const body = (
    <>
      <div style={{ flex: 1, overflowY: 'auto', padding: '10px 8px 8px' }}>
        {NAV_SECTIONS.filter((g) => g.roles.includes(role)).map((g) => (
          <div key={g.group} style={{ marginTop: 8, marginBottom: 4 }}>
            {!(collapsed && !isMobile) && (
              <div
                style={{
                  fontSize: 10,
                  color: 'var(--bx-text-muted)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  fontWeight: 700,
                  padding: '8px 10px 4px',
                }}
              >
                {g.group}
              </div>
            )}
            {g.items.map((it) => {
              const active = route === it.route;
              const indent = it.indent || 0;
              const padLeft = collapsed && !isMobile ? 0 : 10 + indent * 14;
              const btnStyle: CSSProperties = {
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                gap: collapsed && !isMobile ? 0 : 10,
                padding:
                  collapsed && !isMobile ? '10px 0' : `9px 10px 9px ${padLeft}px`,
                justifyContent: collapsed && !isMobile ? 'center' : 'flex-start',
                borderRadius: 8,
                cursor: it.disabled ? 'not-allowed' : 'pointer',
                background: active ? '#eef2ff' : 'transparent',
                color: active
                  ? 'var(--bx-primary)'
                  : it.disabled
                    ? 'var(--bx-text-muted)'
                    : 'var(--bx-text-body)',
                fontWeight: active ? 600 : 500,
                fontSize: 13,
                marginBottom: 1,
                border: 'none',
                textAlign: 'left',
                fontFamily: 'var(--bx-font-sans)',
                opacity: it.disabled ? 0.55 : 1,
                whiteSpace: 'nowrap',
                position: 'relative',
              };
              return (
                <button
                  key={it.id}
                  type="button"
                  disabled={it.disabled}
                  onClick={() => !it.disabled && go(it.route)}
                  title={collapsed && !isMobile ? it.label : undefined}
                  style={btnStyle}
                >
                  {indent > 0 && !(collapsed && !isMobile) && (
                    <span
                      aria-hidden
                      style={{
                        position: 'absolute',
                        left: 16,
                        top: 4,
                        bottom: 4,
                        width: 1,
                        background: active ? 'var(--bx-primary)' : 'var(--bx-border-muted)',
                        opacity: active ? 0.5 : 1,
                      }}
                    />
                  )}
                  <Icon name={it.icon} size={15} />
                  {!(collapsed && !isMobile) && (
                    <span
                      style={{
                        flex: 1,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {it.label}
                    </span>
                  )}
                  {!(collapsed && !isMobile) && it.hint && (
                    <span style={{ fontSize: 9.5, color: 'var(--bx-text-muted)' }}>
                      {it.hint}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        ))}
      </div>
      {!isMobile && (
        <div style={{ borderTop: '1px solid var(--bx-border-muted)', padding: 8 }}>
          <button
            type="button"
            onClick={onToggle}
            title={collapsed ? 'Mở rộng' : 'Thu gọn'}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: collapsed ? 'center' : 'flex-start',
              gap: 10,
              padding: collapsed ? '8px 0' : '7px 10px',
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              color: 'var(--bx-text-muted)',
              fontSize: 12,
              borderRadius: 8,
            }}
          >
            <Icon name={collapsed ? 'chevron-right' : 'chevron-left'} size={13} />
            {!collapsed && <span>Thu gọn</span>}
          </button>
        </div>
      )}
    </>
  );

  if (isMobile) {
    return (
      <>
        {drawerOpen && (
          <div
            onClick={onCloseDrawer}
            style={{
              position: 'fixed',
              inset: 0,
              background: 'var(--bx-scrim)',
              zIndex: 70,
            }}
          />
        )}
        <aside
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            bottom: 0,
            width: 260,
            background: '#fff',
            borderRight: '1px solid var(--bx-border-muted)',
            display: 'flex',
            flexDirection: 'column',
            flexShrink: 0,
            transform: drawerOpen ? 'translateX(0)' : 'translateX(-100%)',
            transition: 'transform 220ms var(--bx-ease)',
            zIndex: 80,
            boxShadow: drawerOpen ? 'var(--bx-shadow-pop)' : 'none',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '12px 14px',
              borderBottom: '1px solid var(--bx-border-muted)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: 7,
                  background: 'var(--bx-primary)',
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 700,
                }}
              >
                W
              </div>
              <div
                style={{
                  fontWeight: 700,
                  fontSize: 13.5,
                  color: 'var(--bx-text-heading)',
                }}
              >
                Kế hoạch nhân sự
              </div>
            </div>
            <IconBtn name="x" onClick={onCloseDrawer} title="Đóng" />
          </div>
          {body}
        </aside>
      </>
    );
  }

  return (
    <aside
      style={{
        width: W,
        background: '#fff',
        borderRight: '1px solid var(--bx-border-muted)',
        display: 'flex',
        flexDirection: 'column',
        flexShrink: 0,
        transition: 'width 180ms var(--bx-ease)',
      }}
    >
      {body}
    </aside>
  );
}
